import { User } from 'oidc-client-ts'
import { config, apiUrl } from './config'

/** Read the current access token from oidc-client-ts storage (set by AuthProvider). */
function getAccessToken(): string | null {
  const key = `oidc.user:${config.oidcAuthority}:${config.oidcClientId}`
  const raw = window.localStorage.getItem(key)
  if (!raw) return null
  try {
    return User.fromStorageString(raw).access_token ?? null
  } catch {
    return null
  }
}

/** fetch wrapper that attaches the bearer token and parses JSON. */
export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getAccessToken()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)

  const response = await fetch(apiUrl(path), { ...init, headers })
  if (!response.ok) {
    throw new ApiError(response.status, await response.text().catch(() => ''))
  }
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T
  }
  return (await response.json()) as T
}

function jsonBody(method: string, body: unknown): RequestInit {
  return { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body: unknown) => apiFetch<T>(path, jsonBody('POST', body)),
  put: <T>(path: string, body: unknown) => apiFetch<T>(path, jsonBody('PUT', body)),
  del: (path: string) => apiFetch<void>(path, { method: 'DELETE' }),
}

/** Fetch a binary file with the bearer token and trigger a browser download. */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const token = getAccessToken()
  const headers = new Headers()
  if (token) headers.set('Authorization', `Bearer ${token}`)
  const response = await fetch(apiUrl(path), { headers })
  if (!response.ok) throw new ApiError(response.status, await response.text().catch(() => ''))
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly body: string) {
    super(`API request failed with ${status}`)
    this.name = 'ApiError'
  }
}

/**
 * Human-readable Swedish message from any thrown value. Understands ASP.NET
 * ValidationProblemDetails ({ errors: { Field: [msg] } }), our own { error } shape, a
 * ProblemDetails title, and common HTTP statuses — instead of dumping raw JSON at the user.
 */
export function apiErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const body = safeJsonObject(e.body)
    if (body) {
      const errors = body.errors
      if (errors && typeof errors === 'object') {
        const msgs = Object.values(errors as Record<string, string[]>).flat().map(localizeValidation)
        if (msgs.length) return [...new Set(msgs)].join(' ')
      }
      if (typeof body.error === 'string') return body.error
      if (typeof body.title === 'string' && body.title !== 'One or more validation errors occurred.') return body.title
    }
    if (e.status === 401 || e.status === 403) return 'Du saknar behörighet för den här åtgärden.'
    if (e.status === 404) return 'Kunde inte hittas.'
    return e.body?.trim() || `Ett fel uppstod (${e.status}).`
  }
  return e instanceof Error ? e.message : String(e)
}

function safeJsonObject(s: string): Record<string, unknown> | null {
  try {
    const v: unknown = JSON.parse(s)
    return v && typeof v === 'object' ? (v as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/** Translate the common .NET DataAnnotations messages to Swedish; pass through anything else (our own messages are already Swedish). */
function localizeValidation(m: string): string {
  if (/is not a valid e-?mail address/i.test(m)) return 'Ogiltig e-postadress.'
  if (/is required/i.test(m)) return 'Ett obligatoriskt fält saknas.'
  if (/maximum length|must be a string.*length/i.test(m)) return 'Ett fält är för långt.'
  return m
}

export interface MeResponse {
  subject: string | null
  name: string | null
  email: string | null
  tenant: string | null
  roles: string[]
  claims: { type: string; value: string }[]
}

export const getMe = () => apiFetch<MeResponse>('/api/v1/me')
